/**
 * Servicio para consumir la API de releases (backoffice).
 * Las versiones publicadas en el Releases manager del backoffice se exponen aquí
 * y se muestran en About -> Version history del frontend.
 * Test y prod usan APIs (y buckets) distintos para poder subir a test y revisar antes.
 */

const explicitApiUrl = process.env.REACT_APP_RELEASES_API_URL;
const isProduction = process.env.NODE_ENV === 'production';
const isTestEnv = process.env.REACT_APP_IS_TEST_ENV === 'true';
const isTestFromBackendUrl = process.env.REACT_APP_URL_BACKEND_API?.includes('test.medbioinformatics.com');

const RELEASES_API_URL =
  explicitApiUrl ||
  (isProduction && !isTestEnv && !isTestFromBackendUrl
    ? process.env.REACT_APP_RELEASES_API_URL_PROD || 'https://ca346j0lmg.execute-api.eu-central-1.amazonaws.com'
    : process.env.REACT_APP_RELEASES_API_URL_PROD || 'https://ca346j0lmg.execute-api.eu-central-1.amazonaws.com');

export interface ApiReleaseListItem {
  version: string;
  files: {
    statistics: { key: string; url: string; lastModified: string; size: number } | null;
    changelog: { key: string; url: string; lastModified: string; size: number } | null;
  };
}

export interface ApiReleaseStatisticsEntry {
  dataset: string;
  count: number;
}

export interface ApiChangelogEntry {
  Version: string;
  Section: string;
  Text: string;
  releaseDate: string;
  gda_summary?: string;
  vda_summary?: string;
}

export interface ApiReleaseDetail {
  version: string;
  files: ApiReleaseListItem['files'];
  statistics: ApiReleaseStatisticsEntry[] | null;
  changelog: ApiChangelogEntry[] | null;
}

async function getReleasesList(): Promise<ApiReleaseListItem[]> {
  const res = await fetch(`${RELEASES_API_URL}/releases`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Releases API error: ${res.status}`);
  }
  const data = await res.json();
  return data.releases ?? [];
}

async function getRelease(version: string): Promise<ApiReleaseDetail> {
  const res = await fetch(
    `${RELEASES_API_URL}/releases/${encodeURIComponent(version)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }
  );
  if (!res.ok) {
    throw new Error(`Release ${version} API error: ${res.status}`);
  }
  return res.json();
}

export const releasesApiService = {
  getReleasesList,
  getRelease,
  getBaseUrl: () => RELEASES_API_URL,
};

export default releasesApiService;
